import { useEffect, useState } from "react";
import { C } from "./theme.js";
import { StoredImage } from "./ui.jsx";
import { fileUrl } from "./lib/api.js";

/* ============================================================================
   תמונה מוגדלת על כל המסך — עם הגדלה.

   צילום מונה מהטלפון הוא תמונה גדולה שבה הספרות קטנות; "להציג בגודל
   המסך" לא מספיק כדי לקרוא 5 ספרות על מונה חשמל. לכן: כפתורי +/−,
   לחיצה כפולה, גלגלת, וצביטה בטלפון (touch-action מאפשר pinch-zoom
   של הדפדפן בתוך המכל הגולל). אותו רכיב לבעל הנכס ולדייר — ההבדל
   היחיד הוא scope (מאיפה נטענת התמונה).
   ============================================================================ */

const ZOOMS = [1, 1.5, 2, 3, 4];

export default function ZoomLightbox({ id, scope = "owner", onClose, title = "תמונה מוגדלת" }) {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") zoomIn();
      if (e.key === "-") zoomOut();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const zoomIn = () => setZoom((z) => ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, ZOOMS.indexOf(z)) + 1)]);
  const zoomOut = () => setZoom((z) => ZOOMS[Math.max(0, ZOOMS.indexOf(z) - 1)]);
  const onWheel = (e) => {
    if (!e.ctrlKey && Math.abs(e.deltaY) < 2) return;
    if (e.deltaY < 0) zoomIn(); else zoomOut();
  };

  const btn = { background: "#fff", color: C.ink, border: "none" };

  return (
    <div role="dialog" aria-label={title} className="fixed inset-0" style={{ background: "rgba(0,0,0,0.9)", zIndex: 100 }}>
      {/* סרגל — תמיד גלוי, גם כשהתמונה גדולה מהמסך. */}
      <div className="absolute top-0 inset-x-0 flex flex-wrap items-center justify-between gap-2 p-3" style={{ zIndex: 2 }}>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={zoomOut} aria-label="הקטנה" className="rounded-full w-9 h-9 text-lg" style={btn}>−</button>
          <button type="button" onClick={() => setZoom(1)} aria-label="גודל רגיל" className="rounded-full px-3 h-9 text-sm" style={btn}>
            {Math.round(zoom * 100)}%
          </button>
          <button type="button" onClick={zoomIn} aria-label="הגדלה" className="rounded-full w-9 h-9 text-lg" style={btn}>+</button>
          <a href={fileUrl(id, scope)} target="_blank" rel="noreferrer" className="rounded-full px-3 h-9 text-sm flex items-center" style={btn}>
            פתיחה בחלון חדש
          </a>
        </div>
        <button type="button" onClick={onClose} aria-label="סגירה" className="rounded-full px-3 h-9 text-sm" style={btn}>✕ סגירה</button>
      </div>

      {/* המכל הגולל. לחיצה על הרקע (לא על התמונה) סוגרת. */}
      <div className="absolute inset-0 overflow-auto" style={{ paddingTop: 60, touchAction: "pan-x pan-y pinch-zoom" }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} onWheel={onWheel}>
        <div style={{
          minWidth: "100%", minHeight: "100%", display: "flex", padding: 12, boxSizing: "border-box",
          alignItems: zoom === 1 ? "center" : "flex-start", justifyContent: zoom === 1 ? "center" : "flex-start",
        }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
          <div onDoubleClick={() => setZoom((z) => (z === 1 ? 2.5 : 1))} style={{ width: zoom === 1 ? "auto" : `${zoom * 100}%`, maxWidth: zoom === 1 ? "100%" : "none" }}>
            <StoredImage id={id} scope={scope} eager className="rounded-lg"
              style={{
                width: zoom === 1 ? "auto" : "100%", height: "auto",
                maxWidth: zoom === 1 ? "100%" : "none", maxHeight: zoom === 1 ? "84vh" : "none",
                objectFit: "contain", cursor: zoom === 1 ? "zoom-in" : "zoom-out", display: "block",
              }} />
          </div>
        </div>
      </div>

      <div className="absolute bottom-2 inset-x-0 text-center text-[11px]" style={{ color: "rgba(255,255,255,0.8)", pointerEvents: "none" }}>
        + / − להגדלה · לחיצה כפולה ×2.5 · בטלפון אפשר גם לצבוט
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- מארח גלובלי ---
   תמונות מופיעות בהרבה מקומות (תיבת ההודעות, מונים, כרטיס הנכס). במקום
   מצב lightbox בכל רכיב — אירוע אחד: openLightbox(id, scope) → LightboxHost
   שמורכב פעם אחת בשורש מציג אותו. */

export function openLightbox(id, scope = "owner") {
  if (!id) return;
  window.dispatchEvent(new CustomEvent("lightbox:open", { detail: { id, scope } }));
}

export function LightboxHost() {
  const [cur, setCur] = useState(null);
  useEffect(() => {
    const on = (e) => setCur(e.detail || null);
    window.addEventListener("lightbox:open", on);
    return () => window.removeEventListener("lightbox:open", on);
  }, []);
  if (!cur) return null;
  return <ZoomLightbox id={cur.id} scope={cur.scope} onClose={() => setCur(null)} />;
}
